import React, { useRef, useState, useEffect } from "react";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { ReactComponent as UploadButtonIcon } from "../../../assets/svgs/Upload 01.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/delete.svg";

const AddCustomerDocument = ({ onOpen, onClose, onSave }) => {
    const [fileName, setfileName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // Panning State
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const fileInputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) processFile(file);
    };

    const processFile = (file) => {
        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setErrorMessage("");
        resetView();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    };

    const clearFile = (e) => {
        if (e) e.stopPropagation();
        setSelectedFile(null);
        setPreviewUrl("");
        resetView();
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const resetView = () => {
        setPosition({ x: 0, y: 0 });
    };

    // Mouse Event Handlers for Panning
    const handleMouseDown = (e) => {
        if (!previewUrl || !isImage()) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleSave = () => {
        if (!fileName.trim()) {
            setErrorMessage("Document name is required");
            return;
        }
        if (!selectedFile) {
            setErrorMessage("Please upload a document");
            return;
        }
        setErrorMessage("");
        if (onSave) onSave({ fileName, file: selectedFile });
        handleCancel();
    };

    const handleCancel = () => {
        setfileName("");
        setSelectedFile(null);
        setPreviewUrl("");
        setErrorMessage("");
        resetView();
        onClose();
    };

    const isImage = () => selectedFile?.type.startsWith("image/");
    const isPdf = () => selectedFile?.type === "application/pdf";
    const isExcel = () => {
        const type = selectedFile?.type;
        const name = selectedFile?.name || "";
        return (
            type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            type === "application/vnd.ms-excel" ||
            name.toLowerCase().endsWith(".xlsx") ||
            name.toLowerCase().endsWith(".xls")
        );
    };

    return (
        <DailogBox
            open={onOpen}
            onCancel={handleCancel}
            width="95%"
            maxWidth="550px"
        >
            <div className="p-4">
                <h3 className="text-xl font-bold text-[#06513D] mb-2">Add Document</h3>

                <div className="space-y-6">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700">
                            Enter the document name
                        </label>
                        <input
                            type="text"
                            value={fileName}
                            onChange={(e) => {
                                setfileName(e.target.value);
                                if (errorMessage && e.target.value.trim()) setErrorMessage("");
                            }}
                            placeholder="e.g. Identity Proof, Agreement..."
                            className="w-full border-b border-gray-300 py-1 outline-none focus:border-[#06513D] transition-colors text-gray-700"
                        />
                    </div>

                    {/* Integrated Panning Viewport */}
                    <div
                        className={`relative border-2 border-dashed rounded-xl overflow-hidden h-[300px] flex flex-col items-center justify-center transition-all bg-gray-50/50 
              ${previewUrl ? "border-[#06513D] bg-white shadow-inner" : "border-gray-300 hover:border-[#06513D] hover:bg-gray-50 cursor-pointer"}`}
                        onClick={() => !previewUrl && fileInputRef.current.click()}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        {previewUrl ? (
                            <div className="w-full h-full flex flex-col items-center justify-center relative group overflow-hidden">
                                {/* Controls Overlay */}
                                <div className="absolute top-4 right-4 z-50 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            resetView();
                                        }}
                                        className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] font-bold text-gray-500 hover:bg-gray-50 shadow-sm"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={clearFile}
                                        className="p-1.5 bg-red-500 rounded-lg text-white shadow-sm hover:bg-red-600 transition-all flex items-center justify-center"
                                        title="Remove file"
                                    >
                                        <DeleteIcon className="w-4 h-4 fill-current" />
                                    </button>
                                </div>

                                {isImage() ? (
                                    <div
                                        className={`flex-1 flex items-center justify-center w-full h-full p-4 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                                    >
                                        <div
                                            className="transition-transform duration-75 select-none"
                                            style={{
                                                transform: `translate(${position.x}px, ${position.y}px)`,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="max-w-none rounded-lg shadow-xl"
                                                style={{
                                                    maxHeight: "1200px",
                                                    width: "100%",
                                                    minWidth: "400px",
                                                }}
                                            />
                                        </div>
                                        {/* Interaction Hint */}
                                        {!isDragging && (
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[9px] font-bold px-3 py-1.5 rounded-full pointer-events-none backdrop-blur-sm animate-pulse uppercase tracking-wider">
                                                🖐️ Click & Drag to Shift View
                                            </div>
                                        )}
                                    </div>
                                ) : isPdf() ? (
                                    <div className="w-full h-full p-1 bg-gray-100 rounded-lg overflow-hidden">
                                        <iframe
                                            src={`${previewUrl}#toolbar=0&navpanes=0`}
                                            className="w-full h-full border-none rounded-md"
                                            title="PDF Preview"
                                        />
                                    </div>
                                ) : isExcel() ? (
                                    <div className="flex flex-col items-center justify-center text-center p-8 bg-green-50/50 backdrop-blur-sm rounded-2xl border border-green-100 shadow-sm w-[80%]">
                                        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4 border border-green-200">
                                            <span className="text-green-600 font-black text-xl">
                                                XLS
                                            </span>
                                        </div>
                                        <p className="text-gray-800 font-bold mb-1 truncate max-w-full px-4">
                                            {selectedFile?.name}
                                        </p>
                                        <p className="text-[10px] text-green-600 font-bold uppercase tracking-widest">
                                            Excel Sheet Ready
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-center p-8 bg-white/50 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-sm">
                                        <div className="w-16 h-16 bg-[#EBF5F6] rounded-2xl flex items-center justify-center mb-4">
                                            <UploadButtonIcon className="w-8 h-8 opacity-40" />
                                        </div>
                                        <p className="text-gray-800 font-bold mb-1 truncate max-w-[200px]">
                                            {selectedFile?.name}
                                        </p>
                                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                                            Document Ready
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center text-center p-10 animate-fade-in pointer-events-none">
                                <div className="w-16 h-16 bg-[#EBF5F6] rounded-2xl flex items-center justify-center mb-6 shadow-sm shadow-[#06513D]/10">
                                    <UploadButtonIcon className="w-8 h-8" />
                                </div>
                                <p className="text-gray-800 text-lg font-bold mb-2 tracking-tight">
                                    Select Document to Upload
                                </p>
                                <p className="text-gray-400 text-sm max-w-[200px] leading-relaxed">
                                    Drag and drop your file or{" "}
                                    <span className="text-[#06513D] font-bold underline decoration-2 underline-offset-4 pointer-events-auto">
                                        browse
                                    </span>
                                </p>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileChange}
                            accept="application/pdf,image/*,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        />
                    </div>

                    {errorMessage && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-center justify-center gap-2 animate-shake">
                            <span className="text-red-600 text-sm font-semibold italic">
                                ⚠️ {errorMessage}
                            </span>
                        </div>
                    )}

                    <div className="flex justify-center gap-4 pt-4">
                        <CustomButton
                            buttonType="button"
                            variant="cancel"
                            buttonText="Cancel"
                            className="h-[52px] w-[140px] font-bold tracking-wide"
                            onClick={handleCancel}
                        />
                        <CustomButton
                            buttonType="button"
                            variant="ok"
                            buttonText="Upload Now"
                            className="h-[52px] w-[180px] font-bold tracking-wide shadow-lg shadow-[#06513D]/20"
                            onClick={handleSave}
                        />
                    </div>
                </div>
            </div>
        </DailogBox>
    );
};

export default AddCustomerDocument;
